import { useState } from 'react';
import styled from 'styled-components';
import { device } from '../../utils/styled-component-mixins';
import useMediaQuery from '../custom-hooks/useMediaQuery';
import useIsMounted from '../custom-hooks/useIsMounted';
import SvgFromFilePath from '../shared/SvgFromFilePath';

const TiledLinkWrapper = styled.article`
  margin-bottom: 2.5rem;
  overflow: hidden;
  position: relative;
  // iOS 16 z-index fix
  -webkit-transform: translate3d(0, 0, 0);

  &:last-child {
    margin-bottom: 0;
    z-index: 100;
  }

  @media ${device.desktop} {
    filter: none;
    margin-bottom: 0;
  }

  * {
    color: inherit;
    text-decoration: none;
  }

  a {
    display: flex;
    flex-direction: column;
    padding: 1.2rem 2rem 1.6rem 1.5rem;
    overflow: hidden;
    ${(props) =>
      props.theme === 'light'
        ? 'background-color: var(--white);'
        : 'background-color: var(--lighter-black);'}
    ${(props) => props.theme === 'dark' && 'color: var(--white);'}

    &:focus {
      outline: 2px solid var(--browser-outline-blue);
      outline-offset: -2px;
    }

    @media ${device.desktop} {
      height: 100%;
      padding: 3.5rem 4.5rem 2.5rem 3.2rem;

      &:after {
        background-color: var(--grey);
        content: '';
        height: 100%;
        position: absolute;
        right: 200%;
        top: 0;
        transform: skewX(50deg);
        transition: all 0.3s ease-in;
        width: 325%;
        z-index: 1;
      }

      &.hover-in.mounted {
        animation: translate-right 0.3s forwards;

        &::after {
          animation: translate-right 0.3s forwards;
          background-color: var(--slate);
        }
      }

      &.hover-out.mounted {
        animation: translate-left 0.3s forwards;

        &::after {
          animation: translate-left 0.3s forwards;
        }
      }
    }

    &.hover-in {
      svg {
        ${(props) => props.theme === 'light' && 'fill: var(--white)'}
      }

      .tiled-link-content {
        color: var(--white);
      }
    }
  }

  .svg-wrapper {
    height: 4rem;
    margin-left: auto;
    width: 4rem;

    svg {
      ${(props) => (props.theme === 'light' ? 'fill: var(--charcoal)' : 'fill: var(--white);')}
    }

    @media ${device.desktop} {
      display: flex;
      height: 5.5rem;
      justify-content: flex-end;
      margin-bottom: 2rem;
      width: 5.5rem;
      z-index: 100;

      svg {
        transition: all 0.3s ease-in-out;
      }
    }
  }

  .tiled-link-content {
    overflow: hidden;
    transition: all 0.3s ease-in-out;
    z-index: 100;

    @media ${device.desktop} {
      display: flex;
      flex-direction: column;
      height: 100%;
      justify-content: space-between;
      padding-top: 1rem;
    }

    h3 {
      font-size: 1.6rem;
      line-height: 3.2rem;
      margin-bottom: 1rem;

      @media ${device.desktop} {
        font-size: 2.2rem;
        margin-bottom: 1.6rem;
      }
    }

    p {
      font-size: 1.4rem;
      line-height: 2.4rem;
      margin-bottom: 2rem;
    }

    .cta {
      align-items: center;
      font-weight: bold;
      display: flex;
      font-size: 1.4rem;
      line-height: 1.8rem;
      text-decoration: none;
      ${(props) => (props.theme === 'light' ? 'color: var(--cta-blue);' : 'color: var(--white);')}

      span:last-child {
        display: none;
        font-size: 3.2rem;
      }

      @media ${device.desktop} {
        transition: all 0.3s ease-in;

        span:first-child {
          color: var(--white);
          transition: all 0.3s ease-in;
        }

        span:last-child {
          display: block;
        }

        &.hover-in {
          color: var(--white);
          transform: translateX(0);
        }

        &.hover-out {
          transform: translateX(-80px);
        }
      }
    }
  }

  @keyframes translate-right {
    from {
      right: 210%;
    }

    to {
      right: -110%;
    }
  }

  @keyframes translate-left {
    from {
      right: -110%;
    }

    to {
      right: 210%;
    }
  }
`;

const TiledLink = (props) => {
  const [isHovered, setIsHovered] = useState(false);
  const isMounted = useIsMounted();

  const isDesktop = useMediaQuery(device.desktop);

  return (
    <TiledLinkWrapper
      onMouseEnter={() => setIsHovered(!!isDesktop)}
      onMouseLeave={() => setIsHovered(false)}
      theme={props.lightDarkTheme.toLowerCase()}
    >
      <a
        className={`non-default-link ${isHovered ? 'hover-in' : 'hover-out'} ${
          isMounted && 'mounted'
        }`}
        href={props.slug}
        aria-label={`Read more about ${props.pageTitle}`}
      >
        <SvgFromFilePath path={props.cardIcon} />

        <div className="tiled-link-content">
          <div>
            <h3>{props.pageTitle}</h3>
            <p>{props.description}</p>
          </div>
          <div className={`cta ${isHovered ? 'hover-in' : 'hover-out'}`}>
            <span>Read More</span>
            <span className="material-icons-round" aria-hidden="true">
              chevron_right
            </span>
          </div>
        </div>
      </a>
    </TiledLinkWrapper>
  );
};

export default TiledLink;
